import { FM } from './config.js';
import { initHTML } from './init-bootstrap-html.js';
import { getLocale } from './locale.js';

import { entries as manifestDesktop } from '/dist/app/module/desktop/manifest.json';
import { entries as manifestMobile } from '/dist/app/module/mobile/manifest.json';

/*global top, self, window, document, location, navigator, setTimeout, URL */
const doc = document;
const win = window;
const loc = location;

if (top !== self) {
    // Frame buster.
    top.location.replace(loc);
} else if (!PRODUCTION) {
    const isMobile = FM.isMobile;
    doc.documentElement.classList.add(isMobile ? 'is-mobile' : 'is-desktop');
    initHTML();
    win.DESKTOP = !FM.isMobile;
    win.MOBILE = FM.isMobile;
    win.FASTMAIL = true;
    win.FM = FM;
    FM.version = '';
    FM.manifest = {};
    FM.preload = () => {};
    FM.import = (x) => x();
    const sessions = FM.sessions;
    const userId = FM.userId;
    const session =
        sessions && userId && sessions.find((x) => x.userId === userId);
    const language = (session && session.language) || 'en-gb';
    import(/* @vite-ignore */ `/app/locale/${language}.js`);
} else if (loc.protocol === 'https:' && typeof BigInt64Array === 'function') {
    const isMobile = FM.isMobile;
    const manifest = isMobile ? manifestMobile : manifestDesktop;
    const pathToModules = `/app/module/${isMobile ? 'mobile' : 'desktop'}/`;
    const relativePathToModules = '..' + pathToModules;
    const absolutePathToModules = FM.pathToDist + pathToModules;
    const ua = navigator.userAgent;
    const isWebkit =
        (/AppleWebKit/.test(ua) && !/Chrome/.test(ua)) ||
        /\biP(?:ad|hone|od)\b/.test(ua);
    // Preloading is of marginal utility anyway with the service worker, and
    // in Safari I'm seeing weird behaviour where it seems to abort the preload,
    // or not use it, or try to load it without the service worker, or …
    // something. Let's try just disabling it there.
    const preloadModule = isWebkit
        ? () => {}
        : (name) => {
              const link = doc.createElement('link');
              link.rel = 'modulepreload';
              link.href = absolutePathToModules + name;
              doc.head.appendChild(link);
              setTimeout(() => doc.head.removeChild(link), 0);
          };
    const _import = (x, name, timeout) => {
        const module = manifest[name];
        if (module) {
            module.dependencies.forEach(preloadModule);
        }
        return x().catch(
            () =>
                new Promise((resolve) => {
                    // !name => loadLangModule (initial file). If we've somehow
                    // managed to load an old version of the bootstrap HTML and
                    // don't have the JS files cached (for some unexplained
                    // reason), they may not even be present on the server and
                    // so fail. If we've tried a few times and failed, try
                    // doing a cold boot (loading bootstrap page from the web)
                    // to make sure we have the latest version.
                    if (timeout >= 8000 && !name) {
                        const url = new URL(location);
                        if (!url.searchParams.get('coldboot')) {
                            url.searchParams.set('coldboot', '1');
                            location.assign(url.href);
                        }
                    }
                    if (document.visibilityState === 'visible') {
                        timeout = timeout ? Math.min(2 * timeout, 30000) : 1000;
                        setTimeout(() => {
                            resolve(_import(x, '', timeout));
                        }, timeout);
                    } else {
                        document.addEventListener(
                            'visibilitychange',
                            function retry() {
                                document.removeEventListener(
                                    'visibilitychange',
                                    retry,
                                );
                                resolve(_import(x, ''));
                            },
                        );
                    }
                }),
        );
    };
    const preloadModuleAndDependencies = (name) => {
        const module = manifest[name];
        if (module) {
            module.dependencies.forEach(preloadModule);
            preloadModule(module.file);
        }
    };

    FM.manifest = manifest;
    FM.preload = preloadModuleAndDependencies;
    FM.import = _import;

    win.FastMail = FM; // For the app
    win.FM = FM;

    initHTML();

    const language = getLocale(manifest, FM.sessions, FM.userId);
    const loadLangModule = () =>
        import(/* @vite-ignore */ relativePathToModules + language);
    FM.language = language;

    // Array#findLast is the most recent feature we use; anything that has it
    // will have the other things from ES6+ that we need, so we don’t need to
    // add the weight and load time of core-js for them. Browsers that thus
    // need core-js are approximately: Edge≤96, Firefox≤103, Chrome≤96,
    // Safari≤15.3 (15.4 has it), Opera≤82
    if (!Array.prototype.findLast) {
        _import(() => import('./core-js-modern.js')).then(() =>
            _import(loadLangModule),
        );
        preloadModule(language);
    } else {
        _import(loadLangModule);
    }
    preloadModuleAndDependencies('main');
}
